import Box from '@mui/material/Box';
import type { Extensions } from '@tiptap/core';
import { Placeholder } from '@tiptap/extension-placeholder';
import { EditorContent, useEditor } from '@tiptap/react';
import { html as beautifyHtml } from 'js-beautify';
import { CSSProperties, FC, useCallback, useEffect, useRef, useState } from 'react';

import { EXTENSIONS } from './extensions';
import type { FormatFlagKey } from './format-items';
import { HtmlCodeEditor } from './html-code-editor';
import { richHtmlSx } from './rich-html-styles';
import { RichHtmlToolbar } from './toolbar';

export type { FormatFlagKey } from './format-items';
export { HtmlCodeEditor } from './html-code-editor';
export { richHtmlSx, richHtmlTableSx } from './rich-html-styles';
export { BIO_EXTENSIONS, toVimeoEmbedSrc } from './extensions';

type TRichHtmlEditorProps = {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  style?: CSSProperties;
  enableHtmlMode?: boolean;
  enableImageUpload?: boolean;
  /** Adds a toolbar button that embeds a Vimeo video from an id or share link. */
  enableVideoEmbed?: boolean;
  /** Adds table insert plus row/column editing (the latter only inside a table). */
  enableTable?: boolean;
  dataTestId?: string;
  allowedFormats?: FormatFlagKey[];
  /** Tiptap extension set; defaults to the permissive admin set. Pass a strict one for user-authored content. */
  extensions?: Extensions;
  disabled?: boolean;
};

export const RichHtmlEditor: FC<TRichHtmlEditorProps> = ({
  value,
  onChange,
  placeholder,
  style,
  enableHtmlMode = false,
  enableImageUpload = false,
  enableVideoEmbed = false,
  enableTable = false,
  dataTestId,
  allowedFormats,
  extensions = EXTENSIONS,
  disabled = false,
}) => {
  const [isHtmlMode, setIsHtmlMode] = useState(false);

  // Keep onChange in a ref so the editor's onUpdate (created once) always calls
  // the latest callback even if the parent re-creates it.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const editor = useEditor({
    extensions: [...extensions, Placeholder.configure({ placeholder: placeholder ?? '' })],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor: ed }) => onChangeRef.current(ed.getHTML()),
  });

  useEffect(() => {
    if (!editor || isHtmlMode) {
      return;
    }
    if (editor.getHTML() !== value) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [value, editor, isHtmlMode]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  const handleHtmlChange = useCallback((nextValue: string) => {
    onChangeRef.current(nextValue);
  }, []);

  const handleFormatHtml = useCallback(() => {
    if (!value) {
      return;
    }
    const formatted = beautifyHtml(value, {
      indent_size: 2,
      wrap_line_length: 100,
      preserve_newlines: true,
      max_preserve_newlines: 1,
      end_with_newline: false,
    });
    if (formatted !== value) {
      onChangeRef.current(formatted);
    }
  }, [value]);

  return (
    <Box
      data-testid={dataTestId}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        '& .ProseMirror': { outline: 'none', padding: '8px 12px', minHeight: 120 },
        '& .ProseMirror p.is-editor-empty:first-child::before': {
          content: 'attr(data-placeholder)',
          color: 'text.disabled',
          float: 'left',
          height: 0,
          pointerEvents: 'none',
        },
        ...richHtmlSx,
        ...(disabled && { opacity: 0.6 }),
        ...style,
      }}
    >
      <RichHtmlToolbar
        editor={editor}
        isHtmlMode={isHtmlMode}
        showHtmlToggle={enableHtmlMode}
        showImageUpload={enableImageUpload}
        showVideoEmbed={enableVideoEmbed}
        showTable={enableTable}
        onToggleHtmlMode={() => setIsHtmlMode(prev => !prev)}
        onFormatHtml={handleFormatHtml}
        allowedFormats={allowedFormats}
        disabled={disabled}
      />
      <Box
        sx={{
          height: 230,
          overflow: 'auto',
        }}
      >
        {enableHtmlMode && isHtmlMode ? (
          <HtmlCodeEditor
            value={value}
            onChange={handleHtmlChange}
            placeholder={placeholder}
            language="markup"
            minHeight={180}
            maxHeight={222}
            showHeader={false}
            showFooter={false}
          />
        ) : (
          <EditorContent editor={editor} />
        )}
      </Box>
    </Box>
  );
};
